import React from 'react';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
    Alert,
    Button,
    ButtonBase,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Link,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import {getExpertByUid} from '../../../../experts/expertsData';
import {buildStyledMarkdownComponents} from './sharedMarkdownElements';

export const CITATIONS_MARKER_REGEX = /\[\[citations:([\s\S]*?)]]/g;

export function parseCitationMarkerPayload(rawPayload) {
    if (typeof rawPayload !== 'string' || !rawPayload.trim()) {
        return null;
    }

    try {
        const parsedPayload = JSON.parse(rawPayload);
        const items = Array.isArray(parsedPayload?.items)
            ? parsedPayload.items
                .filter((item) => item && typeof item === 'object')
                .map((item) => ({
                    ...item,
                    uid: item.uid == null ? '' : String(item.uid),
                    indexPath: item.indexPath == null ? '' : String(item.indexPath),
                }))
                .filter((item) => item.uid || item.indexPath)
            : [];

        return {
            ...parsedPayload,
            items,
        };
    } catch (_) {
        return null;
    }
}

/** Exported so other `[[...]]`-marker pipelines resolving to the same "N Citations → dialog"
 * shape (e.g. the agentic pipeline's `[[cite:id,...]]` markers, once resolved against its own
 * `citations` array into this same `{items}` payload shape) can reuse this exact pill without
 * duplicating it — see `../../../agenticRun/components/markdown/AgenticMarkdownComponents.jsx`. */
export const InlineCitationsTrigger = React.memo(function InlineCitationsTrigger({payload, onClick}) {
    const citationCount = payload?.items?.length || 0;

    return (
        <ButtonBase
            component="span"
            onClick={() => onClick?.(payload)}
            aria-label={`Open ${citationCount} citations`}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                mx: 0.5,
                px: 0.75,
                py: 0.125,
                borderRadius: 999,
                border: '1px solid',
                borderColor: 'primary.main',
                bgcolor: 'primary.50',
                color: 'primary.main',
                fontSize: 'calc(0.8125rem * 1.25)',
                fontWeight: 600,
                lineHeight: 1.6,
                verticalAlign: 'middle',
                whiteSpace: 'nowrap',
                '&:hover': {
                    bgcolor: 'primary.100',
                },
                '&:focus-visible': {
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: 2,
                },
            }}
        >
            {citationCount} Citation{citationCount === 1 ? '' : 's'}
        </ButtonBase>
    );
});

export function replaceCitationMarkersInNode(node, onOpenCitations, keyPrefix = 'node') {
    if (typeof node === 'string') {
        const matches = Array.from(node.matchAll(new RegExp(CITATIONS_MARKER_REGEX)));
        if (matches.length === 0) {
            return node;
        }

        const result = [];
        let lastIndex = 0;

        matches.forEach((match, index) => {
            const fullMatch = match[0] || '';
            const rawPayload = match[1] || '';
            const matchIndex = match.index || 0;

            if (matchIndex > lastIndex) {
                result.push(node.slice(lastIndex, matchIndex));
            }

            const parsedPayload = parseCitationMarkerPayload(rawPayload);
            if (!parsedPayload?.items?.length) {
                result.push(fullMatch);
            } else {
                result.push(
                    <InlineCitationsTrigger
                        key={`${keyPrefix}-citations-${index}`}
                        payload={parsedPayload}
                        onClick={onOpenCitations}
                    />
                );
            }

            lastIndex = matchIndex + fullMatch.length;
        });

        if (lastIndex < node.length) {
            result.push(node.slice(lastIndex));
        }

        return result;
    }

    if (Array.isArray(node)) {
        return node.flatMap((child, index) => replaceCitationMarkersInNode(child, onOpenCitations, `${keyPrefix}-${index}`));
    }

    if (React.isValidElement(node) && node.props?.children) {
        return React.cloneElement(node, {
            ...node.props,
            children: replaceCitationMarkersInNode(node.props.children, onOpenCitations, keyPrefix),
        });
    }

    return node;
}

export function buildMarkdownComponents(onOpenCitations) {
    const wrapChildren = (children, keyPrefix) => replaceCitationMarkersInNode(React.Children.toArray(children), onOpenCitations, keyPrefix);
    return buildStyledMarkdownComponents(wrapChildren);
}

function getAuthorDisplayName(author) {
    if (!author || typeof author !== 'object') {
        return 'Unknown author';
    }

    return author.displayName || author.name || author.uid || 'Unknown author';
}

function buildFallbackExpertHandle(displayName) {
    return String(displayName || '')
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '.');
}

function getAuthorHandle(author) {
    if (!author || typeof author !== 'object') {
        return '';
    }

    if (author.handle) {
        return String(author.handle);
    }

    const authorUid = author.uid || author.id || '';
    if (authorUid) {
        const expert = getExpertByUid(authorUid);
        if (expert?.handle) {
            return expert.handle;
        }
    }

    return buildFallbackExpertHandle(getAuthorDisplayName(author));
}

function normalizeCitationResults(responsePayload) {
    const reports = Array.isArray(responsePayload?.data)
        ? responsePayload.data
        : (Array.isArray(responsePayload) ? responsePayload : []);

    return reports
        .filter((report) => report && typeof report === 'object')
        .map((report, reportIndex) => ({
            id: report.uid == null ? `report-${reportIndex}` : String(report.uid),
            uid: report.uid == null ? '' : String(report.uid),
            title: report.title == null || report.title === '' ? 'Untitled report' : String(report.title),
            authors: Array.isArray(report.authors)
                ? report.authors
                    .filter((author) => author && typeof author === 'object')
                    .map((author, authorIndex) => ({
                        id: author.uid || author.id || `${report.uid || reportIndex}-author-${authorIndex}`,
                        uid: author.uid == null ? '' : String(author.uid),
                        displayName: getAuthorDisplayName(author),
                        handle: getAuthorHandle(author),
                    }))
                : [],
            pieces: Array.isArray(report.pieces)
                ? report.pieces
                    .filter((piece) => piece && typeof piece === 'object')
                    .map((piece, pieceIndex) => ({
                        id: `${report.uid || reportIndex}-piece-${piece.indexPath || pieceIndex}`,
                        indexPath: piece.indexPath == null ? '' : String(piece.indexPath),
                        content: piece.content == null ? '' : String(piece.content),
                    }))
                : [],
        }));
}

function CitationResultCard({report}) {
    const reportHref = report.uid ? `/products/${encodeURIComponent(report.uid)}` : '';

    return (
        <Paper variant="outlined" sx={{p: 2}}>
            <Stack spacing={1.5}>
                <Stack spacing={0.75}>
                    {reportHref ? (
                        <Link
                            href={reportHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            color="inherit"
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                width: 'fit-content',
                                maxWidth: '100%'
                            }}
                        >
                            <Typography variant="subtitle1" fontWeight={600} sx={{wordBreak: 'break-word'}}>
                                {report.title}
                            </Typography>
                            <OpenInNewIcon fontSize="inherit"/>
                        </Link>
                    ) : (
                        <Typography variant="subtitle1" fontWeight={600} sx={{wordBreak: 'break-word'}}>
                            {report.title}
                        </Typography>
                    )}

                    <Typography variant="body2" color="text.secondary">
                        {report.pieces.length} matching passage{report.pieces.length === 1 ? '' : 's'}
                    </Typography>
                </Stack>

                {report.authors.length > 0 ? (
                    <Stack spacing={0.75}>
                        <Typography variant="body2" color="text.secondary">Authors</Typography>
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            {report.authors.map((author) => {
                                const authorHref = author.handle
                                    ? `/experts/${encodeURIComponent(author.handle)}`
                                    : '';

                                return authorHref ? (
                                    <Link
                                        key={author.id}
                                        href={authorHref}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        underline="hover"
                                        sx={{display: 'inline-flex', alignItems: 'center', gap: 0.5}}
                                    >
                                        {author.displayName}
                                        <OpenInNewIcon fontSize="inherit"/>
                                    </Link>
                                ) : (
                                    <Typography key={author.id} variant="body2">
                                        {author.displayName}
                                    </Typography>
                                );
                            })}
                        </Stack>
                    </Stack>
                ) : null}

                <Stack spacing={2}>
                    {report.pieces.map((piece, index) => (
                        <>
                            {index > 0 ?
                                <Divider/>
                                :
                                null
                            }
                            <Typography variant="body1" sx={{whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}>
                                {piece.content || 'No passage text returned.'}
                            </Typography>
                        </>
                    ))}
                </Stack>
            </Stack>
        </Paper>
    );
}

export function CitationsPayloadDialog({
                                           open,
                                           payload,
                                           requestStatus,
                                           responsePayload,
                                           errorMessage,
                                           onClose,
                                           onRetry,
                                       }) {
    const reports = normalizeCitationResults(responsePayload);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>Citations</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>

                    {requestStatus === 'loading' ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                            <CircularProgress size={18}/>
                            <Typography variant="body2">Loading citation results…</Typography>
                        </Stack>
                    ) : null}

                    {requestStatus === 'error' ? (
                        <Alert severity="error">{errorMessage || 'Failed to load citation results.'}</Alert>
                    ) : null}

                    {requestStatus === 'success' && reports.length === 0 ? (
                        <Alert severity="info">No matching report passages were returned for these citations.</Alert>
                    ) : null}

                    {reports.length > 0 ? (
                        <Stack spacing={1.5}>
                            {reports.map((report) => <CitationResultCard key={report.id} report={report}/>)}
                        </Stack>
                    ) : null}
                </Stack>
            </DialogContent>
            <DialogActions>
                {requestStatus === 'error' ? (
                    <Button onClick={onRetry} variant="outlined">
                        Retry
                    </Button>
                ) : null}
                <Button onClick={onClose} variant="contained">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}


